import { processViewPartHtml, virtualDir } from "#skyward_global";
import { DEFAULT_CLOSE_BUTTON, ModalDialog, showDialog } from "#skyward_modalLayer";
import { notifyDOMLoad, submitForm } from "#skyward_window";
import { encodeHtml } from "./skyward.base.extensions";
import { disable, enable } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";
import { getSvg } from "./skyward.ui.svg";
import { JQueryInput } from "./types";

const globalSearch = initializeGlobalSearch();

const classNames =
{
    globalSearchBoxWrapper: "globalSearchBoxWrapper",
    globalSearchBox: "globalSearchBox",
    browseSearch: "browseSearch",
    browseSearchBox: "browseSearchBox",
    searchResultsContainer: "searchResultsContainer",
    sectionHeader: "sectionHeader",
    sectionHeaderText: "sectionHeader__text",
    labelAreaClass: "js-labelArea",
    descAreaClass: "js-descriptionArea",
    browseRow: "browseRow",
    searchResult: "searchResult",
    skywardLoadContainer: "skywardLoadContainer",
    skywardLoadContainerSpinner: "skywardLoadContainer__spinner",
    skywardLoadContainerMessage: "skywardLoadContainer__message",
    button: "button",
    buttonIconOnly: "ButtonIconOnly",
    noRecordsInBrowse: "noRecordsInBrowse"
} as const;

const events =
{
    showSearch,
    openNewWindow
};

registerEvents("globalSearch", events);

interface SearchResponse
{
    result: SearchResult[] | null;
}

interface SearchResult
{
    header: string;
    clickAction: string;
    results: { Html: string; Data: Record<string, string>; }[];
}

function showSearch()
{
    globalSearch.show();
}

function openNewWindow(this: JQueryInput)
{
    // Stolen from window.loadContent since it closes the top popup for some reason.
    const data = $(this).data();
    const url = `${virtualDir}${data.url}${data.id !== undefined ? `/${data.id}` : ""}`;
    const formData = $.extend({}, data);
    submitForm(url, formData, { openNewWindow: true });
}

interface GlobalSearch
{
    show: () => void;
}

function initializeGlobalSearch()
{
    let dialog: ModalDialog;
    let searchBox: JQuery<HTMLInputElement>;
    let searchButton: JQuery<HTMLAnchorElement>;
    let searchResultsDiv: JQuery<HTMLDivElement>;
    let allowSearch = true;

    const internalGlobalSearch: GlobalSearch =
    {
        show: function ()
        {
            dialog = showDialog("Global Search", createSearchContainerContents, [DEFAULT_CLOSE_BUTTON], { draggable: true, closeOnClick: false, width: '95%' });

            searchBox.attr("placeholder", "Search"); // Seems like the placeholder attribute must be set after the input is put into the DOM

            searchButton.on("click", async function ()
            {
                if (allowSearch)
                {
                    allowSearch = false;
                    disable(searchBox);
                    disable(searchButton);

                    await performSearch(searchBox.val());

                    allowSearch = true;
                    enable(searchBox);
                    enable(searchButton);
                    searchBox.trigger("focus");
                }
            });

            searchBox.on("keyup", function (event)
            {
                if (allowSearch && event.key === "Enter")
                {
                    searchButton.trigger("click");
                }
            });
        }
    };

    function createSearchContainerContents()
    {
        const wrapper = $("<div>");
        const searchBoxWrapper = $("<div>", { class: `${classNames.globalSearchBoxWrapper} ${classNames.browseSearch}`, role: "search" });
        searchBox = $("<input>", { type: "text", class: `${classNames.globalSearchBox} ${classNames.browseSearchBox}`, autofocus: "true", role: "searchbox", "aria-label": "Global Search" });
        searchButton = $("<a>", { href: "javascript:void(0)", class: `${classNames.button} ${classNames.buttonIconOnly}`, role: "button", "aria-label": "Perform Search" });
        searchResultsDiv = $("<div>", { class: classNames.searchResultsContainer, role: "region", "aria-label": "Search Results", "aria-live": "polite" });

        const svg = getSvg("Search", "BrowseFooter");
        searchButton.append(svg);
        searchBoxWrapper.append(searchBox).append(searchButton);
        wrapper.append(searchBoxWrapper).append(searchResultsDiv);

        return wrapper;
    }

    function createSearchResultHeader(headerLabel: string)
    {
        const div = $("<div>", { class: classNames.sectionHeader });
        const h3 = $("<h3>", { class: classNames.sectionHeaderText }).text(headerLabel);

        return div.append(h3);
    }

    function performSearch(searchText?: string)
    {
        const promise = $.ajax(
            {
                url: `${virtualDir}SkySys/GlobalSearch/Search`,
                type: "POST",
                dataType: "JSON",
                data: { searchText: encodeHtml(searchText) },
                activityIndicator: { hideGlass: true },
                success: function (response: SearchResponse)
                {
                    displaySearchResults(response);
                }
            }
        );

        $.wait(500).done(function ()
        {
            if (promise.state() === "pending")
            {
                searchResultsDiv.html(createLoadingGif());
            }
        });

        return promise;
    }

    async function displaySearchResults(response: SearchResponse)
    {
        const searchResults = response.result ?? [];
        const tempHtml = $("<div>");

        const idPrefix = "searchResult-";

        if (searchResults.length === 0)
        {
            const div = $("<div>", { class: `${classNames.noRecordsInBrowse} ${classNames.browseRow}` });
            const p = $("<p>", { text: "No results found" });
            div.append(p);
            searchResultsDiv.html(div);
            return;
        }

        for (const searchResultArea of searchResults)
        {
            tempHtml.append(createSearchResultHeader(searchResultArea.header));

            for (const resultRecord of searchResultArea.results)
            {
                const piece = $("<a>", { class: classNames.searchResult, attr: resultRecord.Data, href: "javascript:void(0)", target: "_blank" });
                piece.attr("data-click-action", searchResultArea.clickAction);
                
                // Process HTML through processViewPartHtml to handle any script tags
                const processedHtml = await processViewPartHtml(resultRecord.Html);
                piece.append(processedHtml);

                const pieceId = piece.data("id") as string;
                const prefixedId = `${idPrefix}${pieceId}`;

                const labelArea = piece.find(`.${classNames.labelAreaClass}`);
                if (labelArea.length)
                {
                    const labelId = `${prefixedId}-label`;
                    labelArea.attr("id", labelId);
                    piece.attr("aria-labelledby", labelId);
                }

                const descArea = piece.find(`.${classNames.descAreaClass}`);
                if (descArea.length)
                {
                    const descId = `${prefixedId}-desc`;
                    descArea.attr("id", descId);
                    piece.attr("aria-describedby", descId);
                }

                tempHtml.append(piece);
            }
        }

        await searchResultsDiv.viewpartHtml(tempHtml.html());
        notifyDOMLoad(searchResultsDiv);
        dialog.animateModalContents();
    }

    function createLoadingGif()
    {
        const imageContainer = $("<div>", { class: classNames.skywardLoadContainer });
        const image = $("<img>", { class: classNames.skywardLoadContainerSpinner, src: `${virtualDir}content/loading.gif?v=2`, alt: "Loading Image" });
        const loadingText = $("<p>", { class: classNames.skywardLoadContainerMessage, text: "Searching, please wait." });

        return imageContainer.append(image).append(loadingText);
    }

    return internalGlobalSearch;
};
